import type { CompanyDTO } from "../types/company";
import type { CompanyPatchOp } from "../types/company-stream";

/**
 * Applies the patch of a `section` event of the company page stream.
 *
 * Held in two copies of one logic: this one, which the UI applies the stream
 * with, and `applyCompanyPatch` in `api/src/company/company-patch.ts`, which
 * the server rebuilds a followed run with. The API cannot import a runtime
 * value from `@shaarp/common` (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps its own;
 * `api/src/company/company-patch-parity.spec.ts` runs both on one corpus, so a
 * drift fails the suite. Change both files together.
 */

/** Keys a patch never writes: they would reach the object's prototype. */
const UNSAFE_PATHS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

/**
 * `company` with each key of `patch` replaced — removed when its value is
 * `undefined`, which is how a removed key arrives once JSON has dropped it.
 * `company` itself is left as it was.
 */
export function applyCompanyPatch(
  company: CompanyDTO,
  patch: readonly CompanyPatchOp[],
): CompanyDTO {
  const fields: Record<string, unknown> = {
    ...(company as unknown as Record<string, unknown>),
  };
  for (const { path, value } of patch) {
    if (UNSAFE_PATHS.has(path)) continue;
    if (value === undefined) {
      delete fields[path];
    } else {
      fields[path] = value;
    }
  }
  return fields as unknown as CompanyDTO;
}
